import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ColDef, GetRowIdParams, SelectionChangedEvent } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { AgTable } from '../../../components/AgTable/AgTable';
import { multiRowSelectionDefs } from '../../../components/AgTable/rowSelectionDefs';
import { ConfirmDeletionDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { selectableFundsAtom } from '../../../services/state/AppConfigStateJ';
import { portfolioCompaniesAtom } from '../../../services/state/PortCosStateJ';
import { ResetEntityTab, SelectedEntity, useResetReingestion } from './useResetReingestion';

interface EntityRow extends SelectedEntity {
  legalName: string;
  detail: string;
}

const companyColDefs: ColDef<EntityRow>[] = [
  { field: 'name', headerName: 'Name', flex: 1 },
  { field: 'legalName', headerName: 'Legal Name', flex: 1 },
  { field: 'detail', headerName: 'Sector', flex: 1 },
];

const fundColDefs: ColDef<EntityRow>[] = [
  { field: 'name', headerName: 'Name', flex: 1 },
  { field: 'legalName', headerName: 'Legal Name', flex: 1 },
  { field: 'detail', headerName: 'Vintage', width: 140 },
];

export function ResetReingestion() {
  const portfolioCompanies = useAtomValue(portfolioCompaniesAtom);
  const funds = useAtomValue(selectableFundsAtom);
  const {
    activeTab,
    onTabChange,
    selected,
    setSelected,
    reloadImmediately,
    setReloadImmediately,
    confirmOpen,
    setConfirmOpen,
    loading,
    onConfirm,
  } = useResetReingestion();
  const [search, setSearch] = useState('');

  const companyRows = useMemo<EntityRow[]>(
    () =>
      [...portfolioCompanies]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((c) => ({ id: c.id, name: c.name, legalName: c.legalName ?? '', detail: c.sector?.name ?? '' })),
    [portfolioCompanies]
  );

  const fundRows = useMemo<EntityRow[]>(
    () =>
      [...funds]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) => ({
          id: f.id,
          name: f.name,
          legalName: f.legalName ?? '',
          detail: f.vintage ? String(f.vintage) : '',
        })),
    [funds]
  );

  const entityLabel = useMemo(() => {
    const names = selected.map((e) => e.name);
    if (names.length <= 3) {
      return names.join(', ');
    }
    return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
  }, [selected]);

  const handleTabChange = useCallback(
    (_e: React.SyntheticEvent, tab: ResetEntityTab) => {
      onTabChange(tab);
      setSearch('');
    },
    [onTabChange]
  );

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent<EntityRow>) => setSelected(e.api.getSelectedRows()),
    [setSelected]
  );

  const getRowId = useCallback((params: GetRowIdParams<EntityRow>) => String(params.data?.id ?? ''), []);

  return (
    <Stack
      component='section'
      width='100%'
      height='100%'
      padding='8px 12px 12px'
      gap='0.75rem'
      overflow='hidden'
    >
      <Typography variant='h4'>Reset for Re-Ingestion</Typography>
      <Typography variant='body2' color='text.secondary'>
        Mark the selected companies&apos; or funds&apos; ledger transactions as unprocessed so they are
        re-ingested on the next ingestion run. No records are deleted &mdash; re-ingestion overwrites the
        existing transactions. Works regardless of the original data source.
      </Typography>
      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label='Companies' value='companies' />
        <Tab label='Funds' value='funds' />
      </Tabs>
      <Stack direction='row' alignItems='center' gap='1rem'>
        <TextField
          size='small'
          placeholder={activeTab === 'companies' ? 'Search companies' : 'Search funds'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          margin='none'
          sx={{ minWidth: '300px' }}
        />
        <FormControlLabel
          control={
            <Checkbox
              size='small'
              checked={reloadImmediately}
              onChange={(e) => setReloadImmediately(e.target.checked)}
            />
          }
          label='Reload the ledger immediately (otherwise it re-ingests on the next scheduled run)'
          sx={{ marginLeft: 'auto' }}
        />
        <Button
          variant='contained'
          color='error'
          disabled={selected.length === 0}
          onClick={() => setConfirmOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Reset for Re-Ingestion
        </Button>
      </Stack>
      <Box flex={1} minHeight={0}>
        {activeTab === 'companies' && (
          <AgTable
            rowData={companyRows}
            columnDefs={companyColDefs}
            rowSelection={multiRowSelectionDefs}
            onSelectionChanged={onSelectionChanged}
            getRowId={getRowId}
            quickFilterText={search}
          />
        )}
        {activeTab === 'funds' && (
          <AgTable
            rowData={fundRows}
            columnDefs={fundColDefs}
            rowSelection={multiRowSelectionDefs}
            onSelectionChanged={onSelectionChanged}
            getRowId={getRowId}
            quickFilterText={search}
          />
        )}
      </Box>
      <ConfirmDeletionDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={onConfirm}
        loading={loading}
        title='Reset for Re-Ingestion'
        confirmLabel='Reset for Re-Ingestion'
      >
        This marks all ledger transactions for {entityLabel} as unprocessed.{' '}
        {reloadImmediately
          ? 'The ledger will be reloaded immediately.'
          : 'They will be re-ingested on the next ingestion run.'}{' '}
        Existing transactions will be overwritten. No records are deleted. Continue?
      </ConfirmDeletionDialog>
    </Stack>
  );
}

export default ResetReingestion;
